/*
   The detector profile: seven settings that decide what a review checks, each
   defaulting to Automatic.

   Automatic resolves when the review runs — the engagement's own profile first,
   then the deployment default — so the console never decides what it means.
   It shows what was asked for before a run and what was applied after one.

   Every label the console prints for a setting or a value comes from
   `GET /detector-profile/options`, so they live in one place on the server.
   This module holds the shapes and the narrowing from wire data; nothing in it
   knows a label.

   Hand-written until `types/api.gen.ts` is regenerated against a backend that
   serves these routes. Each parser narrows `unknown`, so a payload that
   predates the feature reads as "not recorded" rather than as a wrong value.
*/

/** The seven settings, in the server's order. A key the server adds is ignored until listed here. */
export const DETECTOR_SETTING_KEYS = [
  "wording_standard",
  "typography_fidelity",
  "notation_form",
  "graphics_compare",
  "escalate_undecided",
  "reflow_hyphens",
  "house_rules",
] as const

export type DetectorSettingKey = (typeof DETECTOR_SETTING_KEYS)[number]

/** The value every setting starts at, and the one the server resolves for us. */
export const AUTO = "auto"

/**
 * What a reviewer or an admin asked for. A missing key means Automatic, which
 * is also what the server reads it as.
 */
export type DetectorProfile = Partial<Record<DetectorSettingKey, string>>

/** Where a resolved value came from. */
export type DetectorSource = "explicit" | "engagement" | "default"

/**
 * What a review actually ran with: a concrete value per setting, and where each came from.
 *
 * Partial because a run resolved before a setting existed recorded none for it
 * (`reflow_hyphens` and `house_rules` are newer than most stored profiles): a
 * missing key is "not recorded", never Automatic and never the current default.
 */
export interface DetectorProfileResolved {
  values: Partial<Record<DetectorSettingKey, string>>
  source: Partial<Record<DetectorSettingKey, DetectorSource>>
}

export interface DetectorValueOption {
  value: string
  label: string
  /** One line on what choosing it does. */
  help: string
}

export interface DetectorSettingOption {
  key: DetectorSettingKey
  label: string
  /** In the server's order, Automatic first. */
  values: DetectorValueOption[]
}

export interface DetectorProfileOptions {
  /** In the server's order. Only the approved keys survive narrowing. */
  settings: DetectorSettingOption[]
  /** What Automatic falls back to on this deployment when the engagement sets nothing. */
  deploymentDefault: Partial<Record<DetectorSettingKey, string>>
}

/* ── Narrowing ─────────────────────────────────────────────────────────── */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isSettingKey(value: unknown): value is DetectorSettingKey {
  return typeof value === "string" && (DETECTOR_SETTING_KEYS as readonly string[]).includes(value)
}

function isSource(value: unknown): value is DetectorSource {
  return value === "explicit" || value === "engagement" || value === "default"
}

function text(o: Record<string, unknown>, key: string): string | null {
  const v = o[key]
  return typeof v === "string" ? v : null
}

function valueOption(raw: unknown): DetectorValueOption | null {
  if (!isRecord(raw)) return null
  const value = text(raw, "value")
  if (value === null) return null
  return { value, label: text(raw, "label") ?? value, help: text(raw, "help") ?? "" }
}

function settingOption(raw: unknown): DetectorSettingOption | null {
  if (!isRecord(raw)) return null
  const k = raw["key"]
  if (!isSettingKey(k)) return null
  const values = Array.isArray(raw["values"])
    ? raw["values"].flatMap((v) => {
        const option = valueOption(v)
        return option ? [option] : []
      })
    : []
  return { key: k, label: text(raw, "label") ?? k, values }
}

/** A map of setting → value, keeping only the approved keys with a string value. */
function valueMap(raw: unknown): Partial<Record<DetectorSettingKey, string>> {
  const out: Partial<Record<DetectorSettingKey, string>> = {}
  if (!isRecord(raw)) return out
  for (const key of DETECTOR_SETTING_KEYS) {
    const v = raw[key]
    if (typeof v === "string") out[key] = v
  }
  return out
}

/** `GET /detector-profile/options`, narrowed: `settings` as a list in the server's order, and `default`. */
export function parseDetectorProfileOptions(raw: unknown): DetectorProfileOptions {
  if (!isRecord(raw)) return { settings: [], deploymentDefault: {} }
  const settingsRaw = raw["settings"]
  const settings = Array.isArray(settingsRaw)
    ? settingsRaw.flatMap((s) => {
        const option = settingOption(s)
        return option ? [option] : []
      })
    : []
  return { settings, deploymentDefault: valueMap(raw["default"]) }
}

/** A requested profile off the wire. Absent or malformed reads as all Automatic. */
export function parseDetectorProfile(raw: unknown): DetectorProfile {
  return valueMap(raw)
}

/**
 * A resolved profile off the wire, or null when the run has not resolved one —
 * a review still queued, or one that predates the feature.
 *
 * A profile recorded before some setting existed carries a subset of the keys,
 * and is read as that subset. What is present is held strictly: a known setting
 * whose value is not a string, or whose source is not one of the three, makes
 * the profile malformed rather than quietly dropping the key.
 */
export function parseDetectorProfileResolved(raw: unknown): DetectorProfileResolved | null {
  if (!isRecord(raw)) return null
  const values: Partial<Record<DetectorSettingKey, string>> = {}
  for (const key of DETECTOR_SETTING_KEYS) {
    if (!(key in raw)) continue
    const v = raw[key]
    if (typeof v !== "string") return null
    values[key] = v
  }
  if (Object.keys(values).length === 0) return null
  const source: Partial<Record<DetectorSettingKey, DetectorSource>> = {}
  const sourceRaw = raw["source"]
  if (sourceRaw !== undefined && sourceRaw !== null) {
    if (!isRecord(sourceRaw)) return null
    for (const key of DETECTOR_SETTING_KEYS) {
      if (!(key in sourceRaw)) continue
      const s = sourceRaw[key]
      if (!isSource(s)) return null
      source[key] = s
    }
  }
  return { values, source }
}

/* ── Shaping ───────────────────────────────────────────────────────────── */

/** True when nothing is set away from Automatic. */
export function isAllAuto(profile: DetectorProfile): boolean {
  return DETECTOR_SETTING_KEYS.every((k) => (profile[k] ?? AUTO) === AUTO)
}

/**
 * The partial profile a review is submitted with: only what was set away from
 * Automatic, since a missing key is Automatic. Null when nothing was, so the
 * form field can be left off and an old server sees the request it always did.
 */
export function toSubmittedProfile(profile: DetectorProfile): DetectorProfile | null {
  const out: DetectorProfile = {}
  for (const key of DETECTOR_SETTING_KEYS) {
    const v = profile[key]
    if (v !== undefined && v !== AUTO) out[key] = v
  }
  return Object.keys(out).length > 0 ? out : null
}

/**
 * The whole profile with every key spelled out, Automatic included — what an
 * engagement is saved as, so a save replaces the stored profile rather than
 * merging into it.
 */
export function toFullProfile(profile: DetectorProfile): Record<DetectorSettingKey, string> {
  const out = {} as Record<DetectorSettingKey, string>
  for (const key of DETECTOR_SETTING_KEYS) out[key] = profile[key] ?? AUTO
  return out
}

/**
 * The settings of a derived profile file (`data/client/<batch>/detector_profile.json`):
 * `settings.<key>.value`. Null when the file carries none of them, so a wrong
 * file is refused rather than read as all Automatic.
 */
export function profileFromDerivedFile(raw: unknown): DetectorProfile | null {
  if (!isRecord(raw)) return null
  const settings = raw["settings"]
  if (!isRecord(settings)) return null
  const out: DetectorProfile = {}
  for (const key of DETECTOR_SETTING_KEYS) {
    const entry = settings[key]
    const value = isRecord(entry) ? entry["value"] : entry
    if (typeof value === "string") out[key] = value
  }
  return Object.keys(out).length > 0 ? out : null
}

/* ── Labels, looked up ─────────────────────────────────────────────────── */

export function settingOf(
  options: DetectorProfileOptions,
  key: DetectorSettingKey,
): DetectorSettingOption | undefined {
  return options.settings.find((s) => s.key === key)
}

/** The server's label for a value, or the value itself when the server does not list it. */
export function valueLabel(
  options: DetectorProfileOptions,
  key: DetectorSettingKey,
  value: string,
): string {
  return settingOf(options, key)?.values.find((v) => v.value === value)?.label ?? value
}

/**
 * One line for a collapsed group: "All automatic", or the settings set away
 * from it, by their server labels.
 */
export function profileSummary(profile: DetectorProfile, options: DetectorProfileOptions): string {
  const set = options.settings.flatMap((s) => {
    const v = profile[s.key]
    return v !== undefined && v !== AUTO ? [`${s.label}: ${valueLabel(options, s.key, v)}`] : []
  })
  return set.length === 0 ? "All automatic" : set.join(" · ")
}

/** Where a resolved value came from, as staff read it. Internal: never on client-facing text. */
export const SOURCE_LABEL: Readonly<Record<DetectorSource, string>> = {
  explicit: "set on this review",
  engagement: "from engagement",
  default: "deployment default",
}
